import { NewIndicatorChartData } from './Content/NewIndicatorChartData';
import { NewLevelData } from './Content/NewLevelData';
import { NewObjectiveData } from './Content/NewObjectiveData';
import { NewOpportunitiesData } from './Content/NewOpportunitiesData';
import { NewStateData } from './Content/NewStateData';
import { NewValueWidgetData } from './Content/NewValueWidgetData';
import { NewWidgetGeneral } from './Content/NewWidgetGeneral';

// Every widget type the wizard can create, in the order they are offered.
//
// `steps` holds the type-specific steps that follow the type picker, as i18n keys
// rather than translated strings so nothing is resolved at import time. `defaults`
// seeds a new widget of that type, including the grid footprint it is created at.
// `sample` makes the made-up values the type tile on the first step shows, and
// `sampleData` is merged into the widget's data for it.
// `minSize`, when set, is the smallest footprint the widget can be resized to.

const DEFAULT_STEPS = {
    data: { descriptionKey: 'dashboard.custom_dashboard.data_description', titleKey: 'dashboard.custom_dashboard.data_title' },
    general: { descriptionKey: 'dashboard.custom_dashboard.general_description', titleKey: 'dashboard.custom_dashboard.general_title' }
};

// The type picker draws each type as the real widget fed with these made-up
// values, rather than querying the API just to fill a tile. They are functions so
// that timestamps are relative to when the picker opens.

const HOUR = 3600;

function indicatorChartSample() {
    const
        now = moment().startOf('hour').unix(),
        // A working day: low overnight, peaking around midday.
        profile = [8, 7, 7, 6, 7, 9, 14, 22, 30, 34, 36, 37, 33, 35, 36, 34, 30, 24, 18, 15, 13, 11, 10, 9];

    return {
        points: profile.map((value, index) => [(now - (profile.length - index) * HOUR) * 1000, value * 1000]),
        unit: 'Wh'
    };
}

function objectiveSample() {
    const
        real = [82, 76, 79, 70, 63, 61, 58, 62, 66, null, null, null].map(value => value && value * 1000000),
        targets = [85, 80, 80, 72, 68, 64, 60, 62, 66, 72, 80, 86].map(value => value * 1000000),
        currentMonth = 8,
        sum = values => values.slice(0, currentMonth + 1).reduce((total, value) => total + value, 0),
        actual = sum(real),
        target = sum(targets);

    return {
        isConsumption: true,
        progress: { actual, currentMonth, deviation: (actual - target) / target, real, target, targets },
        unit: 'Wh',
        year: moment().year()
    };
}

function stateSample() {
    const now = moment().unix();

    return {
        result: {
            lastTimestamp: now - 5 * 60,
            segments: [[0.3, 0], [0.25, 1], [0.15, 0], [0.3, 1]].map(([share, state]) => ({ share, state })),
            since: now - 7 * HOUR,
            value: 1
        }
    };
}

function levelSample() {
    return { reading: { t: moment().unix() - 5 * 60, v: 13456.7 }, unit: 'm³' };
}

function opportunitiesSample() {
    const totals = { completed: 18400, ongoing: 7200, pending: 12600 };

    return {
        summary: { open: totals.ongoing + totals.pending, openCount: 7, total: 38200, totals }
    };
}

export const DEFAULT_WIDGET_TYPE = 'value_card';

export const widgetTypes = {
    value_card: {
        sample: () => ({ unit: 'Wh', value: 842600 }),
        sampleData: { decimals: '1' },
        defaults: {
            data: {},
            graphical: {
                icon: 'info',
                palette: 'primary',
                position: { x: 0, y: 0 },
                size: { h: 2, w: 3 },
                visible: true
            },
            source: 'indicator'
        },
        labelKey: 'dashboard.custom_dashboard.value_card',
        steps: [
            { ...DEFAULT_STEPS.data, Component: NewValueWidgetData },
            { ...DEFAULT_STEPS.general, Component: NewWidgetGeneral }
        ]
    },
    indicator_chart: {
        sample: indicatorChartSample,
        defaults: {
            data: { decimals: '2', unitScale: -1 },
            graphical: {
                chartType: 'column',
                icon: 'info',
                palette: 'primary',
                position: { x: 0, y: 0 },
                // Wider and taller than a value card: bars and a dated x-axis need
                // the room to stay readable.
                size: { h: 3, w: 6 },
                visible: true
            },
            source: 'indicator'
        },
        labelKey: 'dashboard.custom_dashboard.indicator_chart',
        steps: [
            { ...DEFAULT_STEPS.data, Component: NewIndicatorChartData },
            { ...DEFAULT_STEPS.general, Component: NewWidgetGeneral }
        ]
    },
    objective: {
        sample: objectiveSample,
        sampleData: { decimals: '0' },
        defaults: {
            data: { decimals: '2', period: 'ytd', unitScale: -1 },
            graphical: {
                icon: 'info',
                palette: 'primary',
                position: { x: 0, y: 0 },
                size: { h: 3, w: 6 },
                visible: true
            },
            source: 'objective'
        },
        labelKey: 'dashboard.custom_dashboard.objective',
        steps: [
            { ...DEFAULT_STEPS.data, Component: NewObjectiveData },
            { ...DEFAULT_STEPS.general, Component: NewWidgetGeneral }
        ]
    },
    opportunities: {
        sample: opportunitiesSample,
        defaults: {
            data: { measure: 'cost', siteId: '', utilityKindId: '' },
            graphical: {
                icon: 'savings',
                palette: 'primary',
                position: { x: 0, y: 0 },
                size: { h: 2, w: 4 },
                visible: true
            },
            source: 'opportunities'
        },
        labelKey: 'dashboard.custom_dashboard.opportunities',
        steps: [
            { ...DEFAULT_STEPS.data, Component: NewOpportunitiesData },
            { ...DEFAULT_STEPS.general, Component: NewWidgetGeneral }
        ]
    },
    state: {
        sample: stateSample,
        defaults: {
            data: { normalValue: 1 },
            graphical: {
                icon: 'info',
                palette: 'primary',
                position: { x: 0, y: 0 },
                size: { h: 2, w: 3 },
                visible: true
            },
            source: 'indicator'
        },
        labelKey: 'dashboard.custom_dashboard.state',
        steps: [
            { ...DEFAULT_STEPS.data, Component: NewStateData },
            { ...DEFAULT_STEPS.general, Component: NewWidgetGeneral }
        ]
    },
    level: {
        sample: levelSample,
        sampleData: { max: 20000, min: 0 },
        defaults: {
            data: { decimals: '1', unitScale: -1 },
            graphical: {
                graphic: 'tank',
                icon: 'info',
                palette: 'primary',
                position: { x: 0, y: 0 },
                size: { h: 2, w: 3 },
                visible: true
            },
            source: 'indicator'
        },
        labelKey: 'dashboard.custom_dashboard.level',
        minSize: { h: 2, w: 3 },
        steps: [
            { ...DEFAULT_STEPS.data, Component: NewLevelData },
            { ...DEFAULT_STEPS.general, Component: NewWidgetGeneral }
        ]
    }
};

// A blank widget of the given type, ready to be fed to the wizard.
export function buildWidget(type) {
    const { defaults } = widgetTypes[type];

    return {
        data: { ...defaults.data },
        graphical: {
            ...defaults.graphical,
            position: { ...defaults.graphical.position },
            size: { ...defaults.graphical.size }
        },
        id: '',
        source: defaults.source,
        subtitle: '',
        title: '',
        type
    };
}

// A widget of the given type with the type's made-up values, as the type picker
// shows it. `sample` goes to the widget component alongside `widget`.
export function sampleWidget(type) {
    const
        { labelKey, sample, sampleData } = widgetTypes[type],
        widget = buildWidget(type);

    return {
        sample: sample(),
        widget: {
            ...widget,
            data: { ...widget.data, ...sampleData },
            // The value card sizes its value by looking itself up by id.
            id: `sample-${type}`,
            legend: i18next.t('dashboard.custom_dashboard.legend_placeholder'),
            title: i18next.t(labelKey)
        }
    };
}

// The type-specific wizard steps, with their titles resolved for the current locale.
export function stepsForType(type) {
    return (widgetTypes[type]?.steps || []).map(({ Component, descriptionKey, titleKey }) => ({
        Component,
        description: i18next.t(descriptionKey),
        title: i18next.t(titleKey)
    }));
}
